// CustomDomainPanel — l'adresse de l'app : celle que la plateforme offre, et celle que son
// propriétaire apporte.
//
// ── CE QUE CET ÉCRAN PROMET ─────────────────────────────────────────────────
//
// Deux lignes à copier chez son registrar, et un état qui se lit sans savoir ce qu'est un CNAME. Il
// ne montre jamais notre infrastructure : ni IP, ni nom de fournisseur, ni le détail de ce que la
// plateforme fait de son côté.
//
// ── AUCUNE VALIDATION ICI, ET C'EST VOULU ───────────────────────────────────
//
// La règle de ce qu'est un domaine acceptable vit côté serveur (platform-service/lib/custom_domain),
// qui rend un message écrit pour la personne. La recopier ici ferait DEUX vérités, et l'écran
// finirait par dire oui à ce que le serveur refuse. Le bouton n'est désactivé que sur un champ vide —
// ce n'est pas une règle sur les domaines, c'est « il n'y a rien à envoyer ».
//
// La traduction d'un état en phrase vit dans lib/domain_status_view.ts, pure et éprouvée : elle
// n'annonce jamais « en ligne » sans que le nom ET son certificat soient actifs, parce qu'un nom
// actif sans certificat donne une erreur HTTPS au visiteur.

import { useEffect, useState } from "react";
import { listDomains, addDomain, removeDomain, type CustomDomain, type DnsLine } from "../api";
import { domainStatusView, pollingDelayMs } from "../lib/domain_status_view";

const TONE_CLASS = {
  live:    "text-ok",
  waiting: "text-text-secondary",
  problem: "text-warn",
} as const;

function DnsLines({ records, apexNote }: { records: DnsLine[]; apexNote: string }) {
  return (
    <div className="mt-3 rounded-md border border-border bg-surface-muted p-3">
      <p className="mb-2 text-[11px] text-text-secondary">
        Créez ces {records.length === 1 ? "ligne" : "lignes"} chez votre registrar (OVH, Gandi, GoDaddy…) :
      </p>
      <div className="min-w-0 overflow-x-auto">
      {/* UN TABLEAU DÉFILE DANS SA BOÎTE, il ne pousse pas la page.
          `w-full` ne borne rien : la largeur INTRINSÈQUE d'un tableau (celle que son
          contenu exige) l'emporte, donc il déborde de son parent et c'est la PAGE qui
          se met à défiler de côté — la panne responsive classique, et la pire, parce
          qu'elle casse tout l'écran et pas seulement le tableau.
          `min-w-0` sur la boîte : sans lui, un enfant de flex refuse de rétrécir sous
          sa largeur intrinsèque et le débordement remonte d'un cran. */}
      <table className="w-full text-left text-[11px]">
        <thead className="text-text-secondary">
          <tr><th className="pb-1 pr-3 font-normal">Type</th><th className="pb-1 pr-3 font-normal">Nom</th><th className="pb-1 font-normal">Valeur</th></tr>
        </thead>
        <tbody className="font-mono">
          {records.map((r) => (
            <tr key={`${r.type}-${r.name}`} className="align-top">
              <td className="pr-3 pt-1">{r.type}</td>
              <td className="pr-3 pt-1 break-all">{r.name}</td>
              <td className="pt-1 break-all">{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {/* Le cas du domaine racine est NOMMÉ plutôt que devinée : on ne peut pas savoir de façon
          fiable si « monsite.fr » en est un (« monsite.co.uk » a trois étiquettes et en est un
          aussi), et un CNAME y est interdit par le standard DNS. */}
      <p className="mt-3 text-[11px] leading-relaxed text-text-secondary">{apexNote}</p>
    </div>
  );
}

export default function CustomDomainPanel({ slug }: { slug: string }) {
  const [freeAddress, setFreeAddress] = useState<string>("");
  const [cnameTarget, setCnameTarget] = useState<string>("");
  const [domains, setDomains] = useState<CustomDomain[] | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [apexNote, setApexNote] = useState<string>("");

  const refresh = async () => {
    try {
      const r = await listDomains(slug);
      setFreeAddress(r.free_address);
      setCnameTarget(r.cname_target);
      setDomains(r.domains);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
      setDomains([]);
    }
  };

  useEffect(() => { void refresh(); }, [slug]);

  // RELECTURE AUTOMATIQUE — le défaut que le premier essai réel a révélé. Le code de validation
  // n'existe pas au moment de l'ajout : il arrive quelques secondes plus tard. Sans ceci, l'écran
  // affichait l'état d'il y a une minute et l'opérateur croyait que rien ne s'était passé, alors
  // que tout avançait. La décision de relire (et celle de S'ARRÊTER quand tout est prêt) vit dans
  // lib/domain_status_view, pure et éprouvée — un minuteur qui ne s'arrête jamais coûterait des
  // requêtes à vide chez nous et chez le fournisseur.
  useEffect(() => {
    if (!domains) return;
    const delay = pollingDelayMs(domains);
    if (delay === null) return;
    const t = setTimeout(() => { void refresh(); }, delay);
    return () => clearTimeout(t);
  }, [domains, slug]);

  const add = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await addDomain(slug, input);
      // La note du domaine racine ne change jamais : elle vient de l'ajout et reste affichée. Les
      // LIGNES, elles, ne sont PAS gardées d'ici : à l'ajout le code de validation n'existe pas
      // encore, donc les figer afficherait une ligne vide pour toujours. Elles sont rendues depuis
      // l'état relu (plus bas), qui les reçoit dès que le fournisseur les produit.
      setApexNote(r.apex_note);
      setInput("");
      await refresh();
    } catch (e: any) {
      // Le message vient du serveur, écrit pour la personne. On l'affiche tel quel : le reformuler
      // ici perdrait ce qu'il dit de précis sur ce qu'il faut corriger.
      setErr(String(e?.message ?? e));
    } finally { setBusy(false); }
  };

  const drop = async (hostname: string) => {
    setBusy(true); setErr(null);
    try { await removeDomain(slug, hostname); await refresh(); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex h-full w-full flex-col gap-4 bg-bg p-5">
      <section>
        <h3 className="text-[11px] uppercase tracking-wide text-text-secondary">Adresse fournie</h3>
        <p className="mt-1 font-mono text-[13px] text-text">{freeAddress || "—"}</p>
        <p className="mt-1 text-[11px] text-text-secondary">Gratuite, en HTTPS, rien à configurer.</p>
      </section>

      <section>
        <h3 className="text-[11px] uppercase tracking-wide text-text-secondary">Votre propre domaine</h3>

        <div className="mt-2 flex items-center gap-2">
          <input
            id={`domain-input-${slug}`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="www.monsite.fr"
            spellCheck={false}
            autoCapitalize="off"
            className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2.5 py-1.5 font-mono text-[12px] text-text"
          />
          {/* Désactivé sur un champ VIDE seulement : « il n'y a rien à envoyer », pas une règle sur
              les domaines — celle-là est au serveur, et une seule fois. */}
          <button type="button" onClick={() => void add()} disabled={busy || input.trim() === ""}
            className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-[12px] text-white disabled:opacity-40">
            Ajouter
          </button>
        </div>

        {err && <p role="alert" className="mt-2 text-[11px] text-warn">{err}</p>}


        <ul className="mt-3 flex flex-col gap-2">
          {domains?.map((d) => {
            const v = domainStatusView(d);
            return (
              <li key={d.hostname} className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate font-mono text-[12px] text-text">{d.hostname}</p>
                  <p className={`text-[11px] ${TONE_CLASS[v.tone]}`}>{v.label}</p>
                  {v.detail && <p className="mt-0.5 text-[11px] text-text-secondary">{v.detail}</p>}
                  {/* Les lignes viennent de l'ÉTAT RELU, jamais de la réponse à l'ajout : le CNAME est
                      connu tout de suite, mais le TXT n'arrive qu'une fois le certificat initialisé. */}
                  {cnameTarget !== "" && (
                    <DnsLines
                      records={[
                        { type: "CNAME", name: d.hostname, value: cnameTarget, why: "dirige les visiteurs vers votre app" },
                        ...(d.txt_name && d.txt_value
                          ? [{ type: "TXT" as const, name: d.txt_name, value: d.txt_value, why: "prouve que ce domaine est le vôtre" }]
                          : []),
                      ]}
                      apexNote={apexNote}
                    />
                  )}
                </div>
                <button type="button" onClick={() => void drop(d.hostname)} disabled={busy}
                  className="shrink-0 rounded-md px-2 py-1 text-[11px] text-text-secondary hover:bg-surface-muted disabled:opacity-40">
                  Retirer
                </button>
              </li>
            );
          })}
        </ul>

        {domains?.length === 0 && (
          <p className="mt-2 text-[11px] text-text-secondary">
            Aucun domaine pour l'instant. Votre app reste joignable à l'adresse ci-dessus.
          </p>
        )}
      </section>
    </div>
  );
}
